/**
 * Utility functions for device detection and identification
 */

export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  deviceType: 'desktop' | 'laptop' | 'mobile' | 'tablet';
  browser: string;
  userAgent: string;
}

/**
 * Generate a unique device ID based on browser fingerprint
 */
function generateDeviceId(): string {
  if (typeof window === 'undefined') return 'unknown';
  
  // Try to get from localStorage first
  const stored = localStorage.getItem('device_id');
  if (stored) return stored;

  // Generate a new device ID
  const deviceId = `device-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  localStorage.setItem('device_id', deviceId);
  return deviceId;
}

/**
 * Detect device type from user agent
 */
function detectDeviceType(userAgent: string): 'desktop' | 'laptop' | 'mobile' | 'tablet' {
  const ua = userAgent.toLowerCase();
  
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
    return 'mobile';
  }
  
  if (/macintosh|mac os x/i.test(ua)) {
    return 'laptop';
  }
  
  return 'desktop';
}

/**
 * Detect browser name from user agent
 */
function detectBrowser(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('edg/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('opera/') || ua.includes('opr/')) return 'Opera';
  if (ua.includes('msie') || ua.includes('trident/')) return 'IE';
  
  return 'Unknown';
}

/**
 * Generate device name from user agent
 */
function generateDeviceName(userAgent: string): string {
  const browser = detectBrowser(userAgent);
  const ua = userAgent.toLowerCase();
  
  let os = 'Unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os x') || ua.includes('macintosh')) os = 'Mac';
  else if (ua.includes('linux')) os = 'Linux';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  
  return `${browser} no ${os}`;
}

/**
 * Get current device information
 */
export function getDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined') {
    return {
      deviceId: 'unknown',
      deviceName: 'Unknown',
      deviceType: 'desktop',
      browser: 'Unknown',
      userAgent: 'Unknown',
    };
  }

  const userAgent = navigator.userAgent;
  const deviceType = detectDeviceType(userAgent);
  const browser = detectBrowser(userAgent);
  const deviceName = generateDeviceName(userAgent);
  const deviceId = generateDeviceId();

  return {
    deviceId,
    deviceName,
    deviceType,
    browser,
    userAgent,
  };
}
